import React, { useContext, useRef } from 'react'
import cx from 'classnames'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import Breadcrumb from 'src/components/Breadcrumb'
import { PageHeaderContext } from './pageHeaderContext'
import DocumentTitleUpdater from 'src/components/DocumentTitleUpdater'
import { NSSEP } from 'src/utils/translations'
import useTranslations from 'src/hooks/useTranslations'
import { TitleLoader } from 'src/components/Loader'

// Style
import baseStyles from 'src/assets/styles/base.module.scss'

/**
 * PageHeader component
 * @param props
 * @returns {ReactPortal|null}
 * @constructor
 */
function PageHeader(props) {
    const pageBreadcrumbPortal = useRef(
        document.getElementById('page-breadcrumb-portal-mfe')
    )?.current
    const pageTitlePortal = useRef(
        document.getElementById('page-title-portal-mfe')
    )?.current
    const pageHeaderContext = useContext(PageHeaderContext)
    const {
        breadcrumbs,
        path,
        routeName,
        title,
        loading,
        setPageHeaderActionsContainer,
        setPageHeaderTitleExtras,
    } = pageHeaderContext
    const { preContent, postContent, className, children, translationParams } =
        props
    const { t, i18n } = useTranslations(`route.${routeName}`, 'PageHeader')

    if (!pageBreadcrumbPortal && !pageTitlePortal) {
        return null
    }

    if (title?.loading || loading) {
        return <TitleLoader width={400} />
    }

    const getTranslatedTitle = () => {
        if (title?.noTranslation) {
            return title.label
        }
        if (
            !i18n.isInitialized ||
            !i18n.hasLoadedNamespace(`route.${routeName}`)
        ) {
            return title.label
        }
        if (typeof title.label === 'string') {
            if (i18n.language === 'en' && !translationParams) {
                return title.label
            }
            return t(`route.${routeName}${NSSEP}${title.label}`, {
                ...translationParams,
                defaultValue: title.label,
            })
        }
        return title.label
    }

    const translatedTitle = getTranslatedTitle()
    const documentTitle =
        typeof translatedTitle === 'string' ? translatedTitle : title?.text
    return (
        <>
            {pageBreadcrumbPortal &&
                ReactDOM.createPortal(
                    <React.Suspense fallback={<TitleLoader width={400} />}>
                        <div className={className}>
                            {breadcrumbs?.length ? (
                                <Breadcrumb path={path} />
                            ) : null}
                            {children}
                        </div>
                    </React.Suspense>,
                    pageBreadcrumbPortal
                )}
            {pageTitlePortal &&
                ReactDOM.createPortal(
                    <React.Suspense fallback={<TitleLoader width={400} />}>
                        <DocumentTitleUpdater routeTitle={documentTitle} />
                        <div className={baseStyles.titleArea}>
                            <div
                                className={cx(baseStyles.titleWrapper, {
                                    [baseStyles.truncateTitleWrapper]:
                                        title.truncate,
                                })}
                            >
                                {preContent}
                                <h1
                                    className={cx(baseStyles.contentTitle, {
                                        [baseStyles.truncateTitle]:
                                            title.truncate,
                                    })}
                                >
                                    {translatedTitle}
                                </h1>
                                {postContent}
                            </div>
                            <div
                                className={baseStyles.pageHeaderActions}
                                ref={setPageHeaderActionsContainer}
                            ></div>
                        </div>
                        <div
                            className={cx(baseStyles.pageHeaderTitleExtras, {
                                [baseStyles.noMarginLeft]: !preContent,
                            })}
                            ref={setPageHeaderTitleExtras}
                        ></div>
                    </React.Suspense>,
                    pageTitlePortal
                )}
        </>
    )
}

PageHeader.propTypes = {
    /** Main title of the page */
    title: PropTypes.node,
    /** Renders content before the title. */
    preContent: PropTypes.node,
    /** Renders content after the title. */
    postContent: PropTypes.node,
    /** Adds class to the page header wrapper. */
    className: PropTypes.string,
    /** @ignore */
    children: PropTypes.node,
}

PageHeader.defaultProps = {
    preContent: null,
    postContent: null,
}

export default PageHeader
